import React, { useMemo, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useUser } from '../../components/UserContext';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../../components/table';
import { useComisionLedger } from '../../hooks/useComisionLedger';
import InfoTip from '../../components/InfoTip';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0);

export function MetasPorAfianzadora({ selectedYear }) {
  const { userData } = useUser();
  const { movements } = useComisionLedger();
  const [metas, setMetas] = useState(null);
  const [selectedAf, setSelectedAf] = useState(null);

  useEffect(() => {
    if (!userData?.org) return undefined;
    const unsub = onSnapshot(
      doc(db, 'orgs', userData.org, 'metas', String(selectedYear)),
      (snap) => setMetas(snap.data()?.metas || null),
      () => setMetas(null)
    );
    return unsub;
  }, [userData?.org, selectedYear]);

  // Signed prima per afianzadora per month, from the ledger (reversals subtract,
  // same as MetasPrimaCard's annual figure).
  const monthlyByAf = useMemo(() => {
    const map = new Map();
    movements.forEach((m) => {
      if (!m.afianzadora || !m.fecha) return;
      const d = new Date(m.fecha);
      if (d.getFullYear() !== selectedYear) return;
      const arr = map.get(m.afianzadora) || Array(12).fill(0);
      arr[d.getMonth()] += Number(m.prima) || 0;
      map.set(m.afianzadora, arr);
    });
    return map;
  }, [movements, selectedYear]);

  // Fraction of the year elapsed — the "you should be here by now" reference.
  const elapsed = useMemo(() => {
    const now = new Date();
    if (selectedYear < now.getFullYear()) return 1;
    if (selectedYear > now.getFullYear()) return 0;
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return (now.getMonth() + now.getDate() / daysInMonth) / 12;
  }, [selectedYear]);

  const rows = useMemo(() => {
    const keys = new Set([...(metas ? Object.keys(metas) : []), ...monthlyByAf.keys()]);
    return Array.from(keys)
      .map((af) => {
        const meta = Number(metas?.[af]) || 0;
        const mensual = monthlyByAf.get(af) || Array(12).fill(0);
        const vendida = mensual.reduce((a, b) => a + b, 0);
        const esperado = meta * elapsed;
        return {
          af,
          meta,
          vendida,
          mensual,
          pct: meta > 0 ? (vendida / meta) * 100 : null,
          esperado,
          delta: meta > 0 ? vendida - esperado : null,
        };
      })
      .filter((r) => r.meta > 0 || r.vendida !== 0)
      .sort((a, b) => (b.meta - a.meta) || (b.vendida - a.vendida));
  }, [metas, monthlyByAf, elapsed]);

  useEffect(() => {
    if (rows.length > 0 && !rows.some((r) => r.af === selectedAf)) {
      setSelectedAf((rows.find((r) => r.meta > 0) || rows[0]).af);
    }
  }, [rows, selectedAf]);

  const chartData = useMemo(() => {
    const row = rows.find((r) => r.af === selectedAf);
    if (!row) return [];
    let acumulada = 0;
    return MONTHS.map((mes, i) => {
      acumulada += row.mensual[i];
      return {
        mes,
        prima: Math.round(row.mensual[i]),
        acumulada: Math.round(acumulada),
        ritmoMeta: row.meta > 0 ? Math.round((row.meta * (i + 1)) / 12) : null,
      };
    });
  }, [rows, selectedAf]);

  if (rows.length === 0) {
    return (
      <div>
        <Heading level={2} className="text-xl text-azul-surelia mb-2">Metas por afianzadora</Heading>
        <Text className="text-sm text-zinc-500 py-6 text-center">
          Sin metas ni prima registrada en {selectedYear}
        </Text>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <Heading level={2} className="text-xl text-azul-surelia">Metas por afianzadora</Heading>
        <Text className="text-sm text-azul-surelia/70">Prima vendida contra la meta anual, {selectedYear}</Text>
      </div>

      <div className="overflow-x-auto border border-azul-surelia/15 rounded-lg mb-6">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Afianzadora</TableHeader>
              <TableHeader className="text-right">Meta anual</TableHeader>
              <TableHeader className="text-right">Vendida</TableHeader>
              <TableHeader className="text-right">Avance</TableHeader>
              <TableHeader className="text-right">
                <span className="inline-flex items-center gap-1">
                  Esperado
                  <InfoTip
                    className="w-3.5 h-3.5 text-zinc-400"
                    text="Meta anual por la fracción del año transcurrido: dónde irías vendiendo a ritmo constante. La diferencia compara lo vendido contra ese punto."
                  />
                </span>
              </TableHeader>
              <TableHeader className="text-right">Diferencia</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow
                key={r.af}
                className={`cursor-pointer hover:bg-azul-surelia/5 ${r.af === selectedAf ? 'bg-azul-surelia/5' : ''}`}
                onClick={() => setSelectedAf(r.af)}
              >
                <TableCell className="py-3">
                  <Text className="text-sm font-medium text-azul-surelia">{r.af}</Text>
                </TableCell>
                <TableCell className="py-3 text-right">
                  <Text className={`text-sm tabular-nums ${r.meta > 0 ? 'text-zinc-700' : 'text-zinc-300'}`}>
                    {r.meta > 0 ? fmtMXN(r.meta) : 'Sin meta'}
                  </Text>
                </TableCell>
                <TableCell className="py-3 text-right">
                  <Text className="text-sm font-semibold text-azul-surelia tabular-nums">{fmtMXN(r.vendida)}</Text>
                </TableCell>
                <TableCell className="py-3 text-right">
                  {r.pct !== null ? (
                    <Text className="text-sm font-semibold text-zinc-700 tabular-nums">{Math.round(r.pct)}%</Text>
                  ) : (
                    <Text className="text-sm text-zinc-300">—</Text>
                  )}
                </TableCell>
                <TableCell className="py-3 text-right">
                  <Text className={`text-sm tabular-nums ${r.meta > 0 ? 'text-zinc-500' : 'text-zinc-300'}`}>
                    {r.meta > 0 ? fmtMXN(r.esperado) : '—'}
                  </Text>
                </TableCell>
                <TableCell className="py-3 text-right">
                  {r.delta !== null ? (
                    <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full tabular-nums ${
                      r.delta >= 0 ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {r.delta >= 0 ? '+' : ''}{fmtMXN(r.delta)}
                    </span>
                  ) : (
                    <Text className="text-sm text-zinc-300">—</Text>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {chartData.length > 0 && (() => {
        const selectedRow = rows.find((r) => r.af === selectedAf);
        const metaSeleccionada = selectedRow?.meta || 0;
        return (
        <div>
          <Text className="text-sm font-medium text-azul-surelia mb-2">{selectedAf} — avance mensual</Text>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value, name) => [fmtMXN(value), name]} />
                <Legend />
                {metaSeleccionada > 0 && (
                  <ReferenceLine
                    y={metaSeleccionada}
                    ifOverflow="extendDomain"
                    stroke="#284865"
                    strokeDasharray="4 4"
                    label={{
                      value: `Meta ${fmtMXN(metaSeleccionada)}`,
                      position: 'insideTopRight',
                      fill: '#284865',
                      fontSize: 11,
                    }}
                  />
                )}
                <Bar dataKey="prima" name="Prima del mes" fill="#53B5CC" />
                <Line type="monotone" dataKey="acumulada" name="Acumulada" stroke="#284865" strokeWidth={2} dot={false} />
                {chartData.some((d) => d.ritmoMeta !== null) && (
                  <Line
                    type="monotone"
                    dataKey="ritmoMeta"
                    name="Ritmo de meta"
                    stroke="#C2F40C"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    dot={false}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        );
      })()}
    </div>
  );
}
